/**
 * `@akademi/site-builder-runtime` — the unified runtime side of the
 * portal: page-renderer + chrome (headers/footers/widgets) + base theme.
 *
 * <h3>What ships here</h3>
 * <ul>
 *   <li><b>Portal</b> (renderer, types, parsers, contexts, theme tokens,
 *       i18n, menu helpers, host-adapter contracts) — the platform
 *       primitives every page needs.</li>
 *   <li><b>Shells</b> (8 public chrome styles, widgets like
 *       LanguageSwitcher / NotificationsBell / LiveSearchBox) — the
 *       operator-pickable header/footer/topbar treatments.</li>
 *   <li><b>Base theme</b> ({@link baseTheme} a.k.a. {@code standardTheme})
 *       — the canonical fallback that every other theme inherits from
 *       for blocks/system they don't override.</li>
 * </ul>
 *
 * <h3>What is NOT here</h3>
 * <ul>
 *   <li>Block components — see {@code @akademi/site-builder-blocks}.</li>
 *   <li>Tenant-private chrome (BTK, etc.) — see
 *       {@code @akademi/tenant-<name>}.</li>
 *   <li>Theme presets (akademi-net, gobito-default, etc.) — see
 *       {@code @akademi/theme-<name>}.</li>
 * </ul>
 *
 * <p>This package is what the previous trio
 * (site-builder-portal + site-builder-shells + theme-corporate-classic)
 * collapses into. All three were always co-installed; merging removes
 * the inter-package import noise and lets the chrome use portal types
 * via relative paths.
 */

// Portal — renderer, types, theme tokens, i18n, contexts, host adapters.
export * from './portal';

// Shells — 8 public chrome styles + widgets.
export {
  SiteShellPresenter,
  DEFAULT_SHELL,
  type SiteShellPresenterProps,
} from './shells/SiteShellPresenter';
export {
  HeaderRenderer,
  Brand,
  TopBar,
  SidebarTopBar,
  SidebarMobileBar,
  isHeaderVisible,
  type HeaderProps,
  type TopBarProps,
} from './shells/headers';
export {
  SidebarCollapsibleHeader,
  type SidebarCollapsibleHeaderProps,
} from './shells/headers/SidebarCollapsibleHeader';
export { FooterRenderer, isFooterVisible, type FooterProps } from './shells/footers';
export {
  registerMenuComponent,
  getMenuComponent,
  listMenuComponents,
  NullMenuComponent,
  type MenuComponent,
  type MenuComponentProps,
} from './shells/menu/menuComponents';
export { DraftBanner } from './shells/DraftBanner';
// Studio preview: the admin ↔ portal protocol behind every host's
// /studio-preview/page and /studio-preview/shell routes.
export {
  PagePreviewHandshake,
  type PagePreviewHandshakeProps,
} from './preview/PagePreviewHandshake';
export { useShellPreviewConfig, type ShellPreviewState } from './preview/useShellPreviewConfig';
export {
  DEV_PREVIEW_ORIGINS,
  announcePreviewReady,
  applyPreviewThemeMode,
  previewAllowedOrigins,
  readPreviewConfigUpdate,
  type PreviewConfigUpdate,
  type PreviewThemeMode,
} from './preview/previewProtocol';
export {
  LeftRail,
  type LeftRailProps,
  type LeftRailConfig,
  type LeftRailItem,
} from './shells/LeftRail';
export {
  ContentModuleShell,
  type ContentModuleShellProps,
  type ContentChromeMode,
} from './shells/ContentModuleShell';
export { ModuleIndexShell, type ModuleIndexShellProps } from './shells/ModuleIndexShell';
export { AnalyticsScripts } from './shells/AnalyticsScripts';
export { ThemeToggle } from './shells/widgets/ThemeToggle';
export { ScrollToTopButton, type ScrollToTopButtonProps } from './shells/widgets/ScrollToTopButton';
export { HelpDeskWidget, type HelpDeskWidgetProps } from './shells/widgets/HelpDeskWidget';
export {
  PillActiveMenuLinks,
  type PillActiveMenuLinksProps,
} from './shells/widgets/PillActiveMenu';
export {
  ProfileMenu,
  ProfileMenuEntries,
  type ProfileMenuItem,
  type ProfileMenuLinkItem,
  type ProfileMenuGroupItem,
  type ProfileMenuProps,
  type ProfileMenuEntriesProps,
} from './shells/widgets/ProfileMenu';
export { LanguageSwitcher, type LanguageSwitcherProps } from './shells/widgets/LanguageSwitcher';
export { MegaMenuLink, type MegaMenuLinkProps } from './shells/widgets/MegaMenuLink';
export { NotificationsBell, type NotificationsBellProps } from './shells/widgets/NotificationsBell';
export {
  FooterResourceList,
  type FooterResourceListProps,
} from './shells/widgets/FooterResourceList';
export { LiveSearchBox, type LiveSearchBoxProps } from './shells/widgets/LiveSearchBox';
export {
  SbCommand,
  SbCommandDialog,
  SbCommandInput,
  SbCommandTabBar,
  SbCommandList,
  SbCommandEmpty,
  SbCommandGroup,
  SbCommandItem,
  SbCommandSeparator,
  SbCommandSeeAll,
  type SbCommandDialogProps,
  type SbCommandInputProps,
  type SbCommandTab,
  type SbCommandTabBarProps,
  type SbCommandItemProps,
} from './shells/widgets/SbCommand';
export { ModuleLiveSearchBox, type SearchKind } from './shells/widgets/ModuleLiveSearchBox';
export {
  SearchIconTriggerClient,
  type SearchIconTriggerClientProps,
} from './shells/widgets/SearchIconTriggerClient';
export { ChatPanel, type ChatPanelProps } from './shells/ChatPanel';
export { ChatWidget, type ChatWidgetProps } from './shells/ChatWidget';
export {
  SidebarIcon,
  BlockIcon,
  listSidebarIcons,
  searchSidebarIcons,
  guessSidebarIcon,
  isLucideIconName,
  looksLikeIconName,
  toIconId,
  listAllLucideIconNames,
  type SidebarIconDef,
} from './shells/icons/sidebarIcons';
export { decodeHtmlPayload } from './portal/htmlPayload';
export { filterPillItems, type PillMenuItem } from './shells/menu/pillMenu';
export {
  hasNonEmptyColumns,
  hasFeaturedPromo,
  hasDropdownContent,
} from './shells/menu/menuDropdown';

// Base theme — the canonical fallback. Empty bundle: the resolver
// falls back to defaultBlocks (genericBlocks from
// @akademi/site-builder-blocks) and route-rendered fallbacks for every
// block/system surface. {@link standardTheme} is the legacy alias kept
// for older imports.
export { baseTheme, standardTheme } from './theme/baseTheme';
